import type { Detection, LetterboxGeometry } from './types';

type Box = { x1: number; y1: number; x2: number; y2: number };

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function iou(a: Box, b: Box): number {
  const x1 = Math.max(a.x1, b.x1);
  const y1 = Math.max(a.y1, b.y1);
  const x2 = Math.min(a.x2, b.x2);
  const y2 = Math.min(a.y2, b.y2);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const areaA = (a.x2 - a.x1) * (a.y2 - a.y1);
  const areaB = (b.x2 - b.x1) * (b.y2 - b.y1);
  return inter / (areaA + areaB - inter + 1e-9);
}

/** 同一クラス内の NMS。入力はスコア降順に並べ替えて処理し、結果も降順。 */
export function nms(boxes: Detection[], thr: number): Detection[] {
  const sorted = [...boxes].sort((a, b) => b.score - a.score);
  const kept: Detection[] = [];
  for (const b of sorted) {
    if (kept.some((k) => k.cls === b.cls && iou(k, b) >= thr)) continue;
    kept.push(b);
  }
  return kept;
}

/**
 * YOLOv8 の生出力 [1, 4+nc, N](または [1, N, 4+nc])を、元フレーム基準の正規化ボックスに変換する。
 * 座標は letterbox 空間(size×size)で出るので、幾何情報で元フレームに戻す。
 */
export function postprocess(
  data: ArrayLike<number>,
  dims: readonly number[],
  geom: LetterboxGeometry,
  opts: { rawConf: number; iou: number },
): Detection[] {
  const d1 = dims[1] ?? 0;
  const d2 = dims[2] ?? 0;
  // [1, C, N](channel-first、ultralytics の生 ONNX 出力)と [1, N, C] を判別する。
  // 実データは N(アンカー数)が C(4+クラス数)よりはるかに大きいため大小比較で判別できるが、
  // 合成テストは候補数(N)が数個しかなく C 以下になり得るため、単純な大小比較では誤判定する。
  // 「クラス数(候補側の値 - 4)は正でなければならない」という制約で一意に決まる場合はそれを優先し、
  // どちらの向きでも成立してしまう(=実データのような N ≫ C の)場合のみ大小比較にフォールバックする。
  const ncIfCF = d1 - 4;
  const ncIfNF = d2 - 4;
  let useCF: boolean;
  if (ncIfCF >= 1 && ncIfNF < 1) useCF = true;
  else if (ncIfNF >= 1 && ncIfCF < 1) useCF = false;
  else useCF = d1 < d2;

  let C: number;
  let N: number;
  let get: (c: number, j: number) => number;
  if (useCF) {
    C = d1; N = d2; get = (c, j) => data[c * N + j] as number;
  } else {
    C = d2; N = d1; get = (c, j) => data[j * C + c] as number;
  }
  const nc = C - 4;
  const cand: Detection[] = [];
  for (let j = 0; j < N; j++) {
    let best = -1;
    let bestCls = 0;
    for (let c = 0; c < nc; c++) {
      const s = get(4 + c, j);
      if (s > best) { best = s; bestCls = c; }
    }
    if (best < opts.rawConf) continue;
    const cx = get(0, j), cy = get(1, j), w = get(2, j), h = get(3, j);
    cand.push({ x1: cx - w / 2, y1: cy - h / 2, x2: cx + w / 2, y2: cy + h / 2, score: best, cls: bestCls });
  }
  return nms(cand, opts.iou).map((b) => ({
    cls: b.cls,
    score: b.score,
    x1: clamp01((b.x1 - geom.dx) / geom.r / geom.sw),
    y1: clamp01((b.y1 - geom.dy) / geom.r / geom.sh),
    x2: clamp01((b.x2 - geom.dx) / geom.r / geom.sw),
    y2: clamp01((b.y2 - geom.dy) / geom.r / geom.sh),
  }));
}
